import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '../../components/language-switcher'
import { authService } from '../../services'
import { ErrorMessage } from './components/error-message'

const pageBackgroundStyle = {
  background:
    'radial-gradient(circle at top left, rgba(125, 211, 252, 0.22), transparent 30%), radial-gradient(circle at 85% 12%, rgba(96, 165, 250, 0.16), transparent 24%), linear-gradient(180deg, #ffffff 0%, #f4faff 38%, #eaf6ff 100%)',
}

const gridBackgroundStyle = {
  backgroundImage:
    'linear-gradient(rgba(186, 230, 253, 0.32) 1px, transparent 1px), linear-gradient(90deg, rgba(186, 230, 253, 0.32) 1px, transparent 1px)',
  backgroundSize: '44px 44px',
  maskImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0.35), transparent 88%)',
  WebkitMaskImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0.35), transparent 88%)',
}

const inputClass =
  'w-full rounded-2xl border border-sky-100 bg-white/90 px-4 py-3 text-[#12304a] shadow-[0_4px_16px_rgba(56,189,248,0.08)] outline-none transition-all duration-200 placeholder:text-slate-400 hover:border-sky-300 focus:border-sky-300 focus:ring-4 focus:ring-sky-200/60 disabled:cursor-not-allowed disabled:opacity-50'

const labelClass = 'block text-sm font-semibold text-[#12304a]'

const primaryButtonClass =
  'w-full rounded-2xl bg-gradient-to-r from-sky-400 to-blue-400 px-6 py-3.5 font-semibold text-white shadow-[0_14px_32px_rgba(56,189,248,0.24)] transition-all duration-200 hover:-translate-y-0.5 hover:brightness-[1.02] hover:shadow-[0_20px_42px_rgba(56,189,248,0.28)] focus:outline-none focus:ring-4 focus:ring-sky-200/70 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0'

const primaryLinkClass =
  'block w-full rounded-2xl bg-gradient-to-r from-sky-400 to-blue-400 px-6 py-3.5 text-center font-semibold !text-white shadow-[0_14px_32px_rgba(56,189,248,0.24)] transition-all duration-200 hover:-translate-y-0.5 hover:brightness-[1.02] hover:shadow-[0_20px_42px_rgba(56,189,248,0.28)] focus:outline-none focus:ring-4 focus:ring-sky-200/70'

const animationStyleHtml = {
  __html: `
    @keyframes slide-up {
      from {
        opacity: 0;
        transform: translateY(24px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .animate-slide-up {
      animation: slide-up 0.55s ease-out;
    }
  `,
}

export default function ForgotPasswordPage() {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string>()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(undefined)
    setLoading(true)

    try {
      await authService.forgotPassword(email.trim())
      setSubmitted(true)
    } catch (err: any) {
      setError(err?.userMessage || err?.response?.data?.message || t('auth.forgotPasswordFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className='relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-10 text-[#12304a]'
      style={pageBackgroundStyle}
    >
      <LanguageSwitcher variant='auth' className='fixed absolute top-4 right-4 z-20' />

      <div className='pointer-events-none fixed inset-0' style={gridBackgroundStyle} />

      <div className='pointer-events-none absolute -right-24 top-20 h-[460px] w-[min(48vw,580px)] rounded-full bg-gradient-to-br from-sky-300/25 to-blue-300/20 blur-3xl' />
      <div className='pointer-events-none absolute -bottom-28 -left-20 h-80 w-80 rounded-full bg-sky-200/30 blur-3xl' />

      <div className='relative z-10 w-full max-w-md animate-slide-up'>
        <div className='rounded-[2rem] border border-sky-200/80 bg-white/90 p-8 shadow-[0_16px_50px_rgba(56,189,248,0.10)] backdrop-blur-2xl transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_20px_56px_rgba(14,165,233,0.14)]'>
          <div className='mb-8 text-center'>
            <h1 className='mb-2 text-3xl font-bold tracking-tight text-slate-950'>
              {t('auth.forgotPasswordTitle')}
            </h1>

            <p className='text-sm text-slate-600'>{t('auth.forgotPasswordHint')}</p>
          </div>

          {submitted ? (
            <div className='space-y-6'>
              <div className='rounded-2xl border border-emerald-200 bg-emerald-50/90 p-4'>
                <p className='text-sm text-emerald-700'>{t('auth.forgotPasswordSuccess')}</p>
              </div>

              <Link to='/login' className={primaryLinkClass}>
                {t('auth.backToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className='space-y-6'>
              <div className='space-y-2'>
                <label htmlFor='email' className={labelClass}>
                  {t('auth.emailAddress')}
                </label>

                <input
                  type='email'
                  id='email'
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  disabled={loading}
                  autoFocus
                  className={inputClass}
                />
              </div>

              {error && <ErrorMessage message={error} />}

              <button type='submit' disabled={loading || !email.trim()} className={primaryButtonClass}>
                {loading ? t('common.processing') : t('auth.forgotPasswordSubmit')}
              </button>

              <div className='text-center'>
                <Link
                  to='/login'
                  className='text-sm font-medium text-slate-600 transition-colors hover:text-sky-700'
                >
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>

      <style dangerouslySetInnerHTML={animationStyleHtml} />
    </div>
  )
}
